import amplitude from 'amplitude-js';
import LogRocket from 'logrocket';

import type { AnalyticsProperties, AnalyticsSink, AnalyticsTrackOptions, AnalyticsUser } from '../types';

/**
 * Amplitude analytics sink and the single Amplitude touchpoint in the codebase.
 * Owns SDK init so every Amplitude call uses the same singleton instance.
 *
 * @param apiKey - Amplitude project API key.
 */
export function createAmplitudeSink(apiKey: string): AnalyticsSink {
  amplitude.getInstance().init(apiKey);

  return {
    name: 'AMPLITUDE',

    track(event: string, properties: AnalyticsProperties = {}, options?: AnalyticsTrackOptions) {
      // Cross-link Amplitude events to the LogRocket session, matching legacy behavior.
      const enriched: AnalyticsProperties = { ...properties };
      if (LogRocket.sessionURL) {
        enriched.sessionUrl = LogRocket.sessionURL;
      }

      if (options?.groups) {
        amplitude.getInstance().logEventWithGroups(event, enriched, options.groups);
      } else {
        amplitude.getInstance().logEvent(event, enriched);
      }
    },

    identify(user: AnalyticsUser) {
      if (user.kind === 'CANDIDATE') {
        if (!user.email) {
          return;
        }
        // Candidate Portal only sets minimal user properties.
        amplitude.getInstance().setUserId(user.email);
        amplitude.getInstance().setUserProperties({
          user_type: 'candidate',
          email: user.email,
        });
        amplitude.getInstance().identify(new amplitude.Identify());
        return;
      }

      if (!user.userId || !user.orgId) {
        return;
      }
      const employeeName = user.employeeName || null;
      const employeeGivenName = employeeName?.split(' ').slice(0, -1).join(' ');
      const employeeFamilyName = employeeName?.split(' ').slice(-1).join(' ');

      amplitude.getInstance().setUserId(user.userId);
      amplitude.getInstance().setGroup('organizationID', user.orgId);
      amplitude.getInstance().setUserProperties({
        user_type: 'employee',
        email: user.userEmail,
        org_id: user.orgId,
        org_name: user.orgName,
        // keeping organization in case any legacy dashboards use it
        organization: user.orgId ? { name: user.orgName, id: user.orgId } : null,
        name: employeeName
          ? {
              full: employeeName,
              given: employeeGivenName,
              family: employeeFamilyName,
            }
          : null,
      });
      amplitude.getInstance().identify(new amplitude.Identify());
    },
  };
}
